import { useQueryClient } from '@tanstack/react-query';
import { Form, message } from 'antd';
import { Store } from 'antd/es/form/interface';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight, ArrowRight, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

import {
  ProductDetailsCard,
  CustomerDetailsCard,
  PackageAndPaymentCard,
  DeliveryInstructionCard,
  OrderIdCard,
  PickupAddressCard,
} from '@/components/OrderForm';
import OrderStepper from '@/components/OrderStepper/OrderStepper';
import { ROUTE } from '@/config';
import { useChangeProduct, useCustomerIO, useUpdateOrderPricePreview } from '@/hooks';
import usePermission from '@/hooks/usePermission';
import {
  useAsyncCreateOrders,
  useCreateContact,
  useCreateOrder,
  useFetchUser,
  useGetAvailableCarriers,
} from '@/hooksApi';
import { useEvaluateShippingRules, useListShippingRules } from '@/hooksApi/useShippingRule';
import { useFetchPickupAddresses } from '@/hooksApi/usePickupAddress';
import { useFetchListProvinces, useFetchAreaByProvinceCode } from '@/hooksApi/useRegion';
import useProductStore from '@/stores/productStore';
import useNewOrderStore from '@/stores/useNewOrderStore';
import {
  AvailableCarrierItem,
  AvailableCarriersAdaptedResponse,
  BasicProduct,
  CarrierPreviewItem,
  ContactRequest,
  Order,
  PaymentType,
  ServiceTypeEnum,
  ShipmentFeeType,
  PermissionName,
} from '@/types';
import { useConfirmModalStore } from '@/stores';
import useOrderTypeStore, { OrderTypeEnum } from '@/stores/useOrderTypeStore';
import {
  adjustProductListByOrderType,
  checkProductValidation,
  getOrderTypeTextKey,
  buildAvailableCarriersRequest,
  processCarrierPreviewResponse,
} from '@/utils';
import { buildEvaluateShippingRulesRequest } from '@/utils/buildEvaluateShippingRulesRequest';
import { decideOrderRouting } from '@/utils/decideOrderRouting';
import { applyRuleDecisionToCarriers } from '@/utils/applyRuleDecisionToCarriers';
import { ContactData } from '@/components/OrderForm/CustomerDetailsCard';
import NoCarrierModal from '@/components/OrderForm/NoCarrierModal';
import { resolveProvinceName } from '@/utils/provinceLookup';
import { buildRestoreFormValues, buildRestoreProducts } from './restoreOrderForm';

const AddNewOrder = () => {
  const [selectedProvinceCode, setSelectedProvinceCode] = useState<string | undefined>(undefined);
  const [addOrderLoading, setAddOrderLoading] = useState<'creating' | 'ready_to_ship' | undefined>(
    undefined,
  );
  const [carrierPreviewList, setCarrierPreviewList] = useState<CarrierPreviewItem[]>([]);
  const [showNoCarrierModal, setShowNoCarrierModal] = useState(false);
  // NEW-2 — when an auto-dispatched/auto-cheapest decision survives the
  // carrier filter with zero carriers (rule mandates X but X is unavailable
  // for this route), surface a different modal with edit-rule + pick-manual
  // CTAs instead of the generic "no carrier enabled" message.
  const [ruleMandateUnavailable, setRuleMandateUnavailable] = useState<{
    ruleName: string;
    carrierName?: string;
  } | null>(null);
  const [currentProcessId, setCurrentProcessId] = useState<string | undefined>(undefined);
  const [recommendedCarrierSlug, setRecommendedCarrierSlug] = useState<string | undefined>(
    undefined,
  );

  const scrollRef = useRef<HTMLDivElement>(null);
  // Gates the auto-derive-fragile effect. Kept false during back-nav restore so
  // the effect cannot clobber the restored order-level `isFragile` before the
  // restored products (with their `is_fragile`) have hydrated. Enabled after a
  // fresh entry immediately, and after a restore once hydration settles.
  const fragileSyncEnabledRef = useRef(false);
  const [isScrolledUp, setIsScrolledUp] = useState(false);

  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  const queryClient = useQueryClient();

  const { orderPayload: existingOrderPayload, savedProvinceCode, setOrderPayload: setStoreOrderPayload, setCarrierSelection, setSelectedCarrier, setSourceRegionUid, setContactDisplayInfo, setSavedProvinceCode, setRuleDispatch, clearEditMode, reset: resetOrderStore } = useNewOrderStore();
  const { mutate: mutateAsyncAddNewOrder } = useAsyncCreateOrders();
  const { mutate: mutateAddNewOrder } = useCreateOrder();
  const { mutate: mutateAddNewContact } = useCreateContact();

  // Separate mutation instances: one for inline preview, one for navigation fetch
  // This prevents the two from racing and overwriting each other's callbacks
  const { mutate: fetchCarrierPreviewInline } = useGetAvailableCarriers();
  const { mutate: fetchCarrierPreviewNavigate, isLoading: carrierPreviewLoading } =
    useGetAvailableCarriers();
  const { mutate: evaluateRulesMutate } = useEvaluateShippingRules();
  // NEW-2 — names lookup for rule-mandate-unavailable modal. Cached by
  // React Query so this is effectively free.
  const { data: rulesListResp } = useListShippingRules();
  const { data: pickupAddressesData } = useFetchPickupAddresses();
  const { data: provinceList } = useFetchListProvinces();
  const { data: areaList } = useFetchAreaByProvinceCode(selectedProvinceCode || '');

  const { data: userData } = useFetchUser();
  const { openConfirm } = useConfirmModalStore();
  const { trackEvent } = useCustomerIO();
  const { hasPermission } = usePermission();

  const { products, resetProducts, setSubmitAttempted } = useProductStore();
  const { orderType, resetOrderType, setOrderType } = useOrderTypeStore();

  // Callback to trigger carrier preview when price preview is requested
  const handleCarrierPreviewTrigger = useCallback(
    (orderData: Order) => {
      // Find the selected pickup address to get source region
      const selectedPickupAddress = pickupAddressesData?.object?.items?.find(
        (addr) =>
          addr.uid === orderData.saved_pick_up_address_uid ||
          addr.uid === orderData.pick_up_address_uid,
      );

      const source_region_uid = selectedPickupAddress?.region?.uid;

      if (!source_region_uid) {
        console.warn('Pickup address not found for carrier preview');
        return;
      }

      if (!orderData.contact?.region_uid) {
        console.warn('Customer region UID missing for carrier preview');
        return;
      }

      // Build carrier preview request using shared utility
      const carrierRequest = buildAvailableCarriersRequest(orderData, source_region_uid);

      fetchCarrierPreviewInline(carrierRequest, {
        onSuccess: (response: AvailableCarriersAdaptedResponse) => {
          const allCarriers = processCarrierPreviewResponse(response);
          setCarrierPreviewList(allCarriers);
          setCurrentProcessId(response.process_id ?? undefined);
          setRecommendedCarrierSlug(response.recommended_carrier_slug ?? undefined);
        },
        onError: (error: any) => {
          console.error('Failed to fetch carrier preview:', error);
          setCarrierPreviewList([]);
          setCurrentProcessId(undefined);
          setRecommendedCarrierSlug(undefined);
          if (error?.response?.data?.code === 760001) {
            setShowNoCarrierModal(true);
          }
        },
      });
    },
    [fetchCarrierPreviewInline, pickupAddressesData],
  );

  const {
    handleFormUpdated,
    orderPrice,
    orderPriceLoading,
    customShipmentFee,
    setCustomShipmentFee,
    setPackageSize,
    transformValuesToOrder,
    checkOrderFormValidation,
    handleProductChanged,
    isPreviewCalled,
  } = useUpdateOrderPricePreview(products, form, false, handleCarrierPreviewTrigger);

  const {
    handleAddProduct,
    handleAddProducts,
    handleDeleteProduct,
    handleUpdateProduct,
    handleUpdateProductBatch,
    setProducts,
    setIsProductListValid,
  } = useChangeProduct(handleProductChanged, orderType);

  useEffect(() => {
    // Only reset form on fresh entry — skip when returning from carrier selection
    if (!existingOrderPayload) {
      // Fresh entry: clear edit mode flag and reset all form/store state
      clearEditMode();
      form.resetFields();
      resetProducts();
      resetOrderType();
      resetOrderStore();
      // Fresh entry: auto-derive-fragile may run normally from the start.
      fragileSyncEnabledRef.current = true;
    } else {
      // Returning from carrier selection — restore form fields from the stored payload
      // Clear stale carrier selection so it gets rebuilt fresh on next "Next" click
      setCarrierSelection(null);

      const contact = existingOrderPayload.contact;
      if (contact) {
        if (savedProvinceCode) {
          setSelectedProvinceCode(savedProvinceCode);
        }
        form.setFieldsValue(buildRestoreFormValues(existingOrderPayload, savedProvinceCode));
        // Keep the Zustand order-type store in sync so OrderTypeCard re-highlights
        // the correct selection (form field alone does not update the store).
        setOrderType((existingOrderPayload.type as OrderTypeEnum) ?? OrderTypeEnum.NORMAL);
      }
      if (existingOrderPayload.products?.length) {
        // Preserve is_fragile / uid / product_catalog_uid so fragility and
        // catalog linkage survive back-nav (see buildRestoreProducts).
        setProducts(buildRestoreProducts(existingOrderPayload.products));
      }

      // Ant Design form.setFieldsValue() does NOT trigger onValuesChange,
      // so manually trigger the price preview after restoring form data
      setTimeout(() => {
        const allValues = form.getFieldsValue();
        handleFormUpdated(
          { region_uid: allValues.region_uid },
          allValues,
        );
        // Re-enable fragile auto-derive only after the restored products and
        // form values have hydrated, so it can't wipe the restored isFragile.
        fragileSyncEnabledRef.current = true;
      }, 0);
    }
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 32;
      setIsScrolledUp(!atBottom);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  // Resolve the source region UID for the currently selected pickup address
  const resolveSourceRegionUid = useCallback(
    (formValues: Store): string | undefined => {
      const selectedPickupAddress = pickupAddressesData?.object?.items?.find(
        (addr) =>
          addr.uid === formValues.saved_pick_up_address_uid ||
          addr.uid === formValues.pick_up_address_uid,
      );
      return selectedPickupAddress?.region?.uid;
    },
    [pickupAddressesData],
  );

  const handleSubmit = (ready_to_ship: boolean) => {
    const formValues = form.getFieldsValue();

    setSubmitAttempted(true);

    // Check for partially filled products (has some data but missing required fields)
    const partiallyFilled = products.filter(
      (p) => {
        const hasTitle = !!p.title;
        const hasPrice = !!p.price || p.price === 0;
        return (hasTitle || hasPrice) && !(hasTitle && hasPrice);
      },
    );

    if (partiallyFilled.length > 0) {
      message.error(t('please_fill_all_product_fields'));
      return;
    }

    // Filter out completely empty products
    const finalProducts = products.filter((p) => checkProductValidation(p));

    const isValid = checkOrderFormValidation({ ...formValues, products: finalProducts });
    setIsProductListValid(finalProducts.length > 0);

    form.submit();
    form
      .validateFields()
      .then((values) => {
        if (isValid) onFinish(values, ready_to_ship, finalProducts);
      })
      .catch((errorInfo) => {
        console.warn('Failed: ', errorInfo);
      });
  };

  const onFinish = (values: Store, ready_to_ship: boolean, finalProducts: BasicProduct[]) => {
    if (userData?.object.verified_at) {
      const orderPayload = transformValuesToOrder(
        { ...values, products: finalProducts },
        ready_to_ship,
        true,
      );

      if (values.saveToAddress) {
        saveContact(orderPayload);
      }

      checkCarriersAndNavigate(orderPayload, ready_to_ship, values);
    } else {
      openNotVerifiedConfirm();
    }
  };

  // Fetch carriers and navigate to carrier selection step.
  //
  // RS-22: before fetching carriers, ask the rule engine what to do.
  // The decision can:
  //   - skip Step 2 entirely (auto-dispatched)
  //   - restrict the carrier list to a rule-allowed subset
  //   - leave the existing flow alone (manual)
  //
  // Rule evaluation failures fall through to the manual flow so the
  // shipping-rules feature never blocks order creation.
  const checkCarriersAndNavigate = (
    orderPayload: Order,
    ready_to_ship: boolean,
    formValues: Store,
  ) => {
    const source_region_uid = resolveSourceRegionUid(formValues);

    if (!source_region_uid || !orderPayload.contact?.region_uid) {
      console.warn('Cannot check carrier availability: missing pickup or destination region.');
      setStoreOrderPayload(orderPayload);
      setShowNoCarrierModal(true);
      return;
    }

    setAddOrderLoading('creating');

    // RC-1 — rule engine matches province_code strings, not region uids.
    // Pull source code from pickup address; destination code is already
    // tracked in form state as `selectedProvinceCode`.
    const sourcePickup = pickupAddressesData?.object?.items?.find(
      (addr) =>
        addr.uid === formValues.saved_pick_up_address_uid ||
        addr.uid === formValues.pick_up_address_uid,
    );
    const sourceProvinceCode = sourcePickup?.region?.province_code || '';
    const destinationProvinceCode = selectedProvinceCode || '';

    const evalReq = buildEvaluateShippingRulesRequest(
      orderPayload,
      sourceProvinceCode,
      destinationProvinceCode,
    );

    evaluateRulesMutate(evalReq, {
      onSuccess: (evalResponse: any) => {
        const decision = decideOrderRouting(evalResponse);
        proceedWithCarrierFetch(orderPayload, ready_to_ship, source_region_uid, decision);
      },
      onError: (err: any) => {
        console.warn('Rule evaluation failed; falling back to manual carrier flow', err);
        proceedWithCarrierFetch(orderPayload, ready_to_ship, source_region_uid, {
          action: 'manual',
          assignedCarrierUid: null,
          winningRuleUid: null,
          restrictedCarrierUids: [],
          recommendedFilter: null,
        });
      },
    } as any);
  };

  const proceedWithCarrierFetch = (
    orderPayload: Order,
    ready_to_ship: boolean,
    source_region_uid: string,
    decision: ReturnType<typeof decideOrderRouting>,
  ) => {
    const carrierRequest = buildAvailableCarriersRequest(orderPayload, source_region_uid);

    fetchCarrierPreviewNavigate(carrierRequest, {
      onSuccess: (response: AvailableCarriersAdaptedResponse) => {
        const allCarriers = processCarrierPreviewResponse(response);

        // F4 — apply the rule decision via the shared reducer (identical to
        // the edit flow) so restricted/auto-dispatch/auto-cheapest filtering
        // + sorting + auto-select-index logic lives in one place.
        const ruleResult = applyRuleDecisionToCarriers(allCarriers, decision);
        const ruleCarriers = ruleResult.carriers;

        setCarrierPreviewList(ruleCarriers);
        setCurrentProcessId(response.process_id ?? undefined);
        setRecommendedCarrierSlug(response.recommended_carrier_slug ?? undefined);

        const hasCarriers = ruleCarriers.length > 0;
        if (!hasCarriers) {
          setAddOrderLoading(undefined);
          setStoreOrderPayload(orderPayload);
          // NEW-2 — when a rule explicitly mandated a carrier (or cheapest
          // filter) but pricing returned nothing matching, surface the
          // explicit mandate-unavailable variant with rule/carrier names.
          if (
            (decision.action === 'auto-dispatched' || decision.action === 'auto-cheapest') &&
            decision.winningRuleUid
          ) {
            const rule = rulesListResp?.object?.find((r) => r.uid === decision.winningRuleUid);
            const ruleName = rule?.name || decision.winningRuleUid;
            const carrierName =
              decision.action === 'auto-dispatched'
                ? rule?.carriers?.find((c) => c.uid === decision.assignedCarrierUid)?.title
                : undefined;
            setRuleMandateUnavailable({ ruleName, carrierName });
          } else {
            setShowNoCarrierModal(true);
          }
          return;
        }

        // Build AvailableCarrierItem[] from the adapted response for the store
        const available: AvailableCarrierItem[] = ruleCarriers.map((c) => ({
          carrier_uid: (c.carrier as any).uid,
          carrier_slug: c.carrier.slug || '',
          carrier_name: c.carrier.title,
          carrier_logo_url: c.carrier.avatar?.abs_url || '',
          shipping_fee: parseFloat(c.shipping_fee) || 0,
          delivery_day: c.serviceType === ServiceTypeEnum.SAME_DAY ? 'same' as const : 'next' as const,
          same_day_cutoff: c.serviceType === ServiceTypeEnum.SAME_DAY ? c.if_ship_before : null,
          drop_off_time: c.serviceType === ServiceTypeEnum.NEXT_DAY ? c.if_ship_before : null,
        }));

        // Save to store and navigate to carrier selection.
        // Clear previously selected carrier so auto-select uses fresh
        // pricing — then re-set when the shared reducer picked an index
        // (auto-dispatched or auto-cheapest).
        setSelectedCarrier(null);
        if (ruleResult.autoSelectIndex !== null && available[ruleResult.autoSelectIndex]) {
          setSelectedCarrier(available[ruleResult.autoSelectIndex]);
        }
        // RS-23 — surface the decision so SelectCarrier can render the
        // auto-dispatch transparency banner. The shared reducer already
        // produced the correct ruleDispatch (null for manual / no-match,
        // populated otherwise — including assignedCarrierSlug for the
        // override-reason modal).
        setRuleDispatch(ruleResult.ruleDispatch);
        setStoreOrderPayload(orderPayload);
        setSourceRegionUid(source_region_uid);
        setSavedProvinceCode(selectedProvinceCode || null);

        // Resolve province/area display names for the summary page
        const provinceName = provinceList?.object?.find(
          (p) => p.province_code === selectedProvinceCode,
        )?.province ?? '';
        const areaName = areaList?.object?.find(
          (a) => a.uid === orderPayload.contact?.region_uid,
        )?.area_name ?? '';
        setContactDisplayInfo({ provinceName, areaName });
        setCarrierSelection({
          process_id: response.process_id,
          recommended_carrier_slug: response.recommended_carrier_slug,
          available_carriers: available,
        });
        setAddOrderLoading(undefined);
        // OBS-1 — when the rule auto-dispatched (single mandated carrier)
        // OR auto-cheapest (rule mandates cheapest filter), Step 2 is
        // redundant: the carrier is already chosen. Skip to summary.
        if (ruleResult.autoSelectIndex !== null && available[ruleResult.autoSelectIndex]) {
          navigate(ROUTE.ORDER_SUMMARY);
        } else {
          navigate(ROUTE.SELECT_CARRIER);
        }
      },
      onError: (error: any) => {
        console.error('Failed to fetch carrier availability on submit:', error);
        setAddOrderLoading(undefined);
        setCurrentProcessId(undefined);
        setRecommendedCarrierSlug(undefined);
        setStoreOrderPayload(orderPayload);
        setShowNoCarrierModal(true);
      },
    });
  };

  const addNewOrderWithProcessId = (
    orderPayload: Order,
    ready_to_ship: boolean,
    processId?: string,
    carrierSlug?: string,
  ) => {
    const enrichedPayload: Order = {
      ...orderPayload,
      ...(processId && carrierSlug
        ? { process_id: processId, selected_carrier_slug: carrierSlug }
        : {}),
    };

    mutateAddNewOrder(enrichedPayload, {
      onSuccess: () => {
        message?.success(t('order_added_successfully'));
        setCurrentProcessId(undefined);
        setRecommendedCarrierSlug(undefined);

        setTimeout(() => {
          if (ready_to_ship) {
            navigate(ROUTE.PICKUPS);
          } else {
            navigate(ROUTE.ORDERS);
          }
          setAddOrderLoading(undefined);
        }, 2000);
      },
      onError: (error: any) => {
        setAddOrderLoading(undefined);
        const errorCode = error?.response?.data?.code;
        const errorMsg = error?.response?.data?.message || '';
        if (errorCode === 300010 || errorMsg.includes('session has expired') || errorMsg.includes('already been used')) {
          setCurrentProcessId(undefined);
          setRecommendedCarrierSlug(undefined);
          console.warn('Carrier process_id expired or already used. Will auto-assign on retry.');
        } else {
          message?.error(t('operation_failed_please_try_again'));
        }
      },
    });
  };

  const addNewOrder = (orderPayload: Order, ready_to_ship: boolean) => {
    addNewOrderWithProcessId(orderPayload, ready_to_ship, currentProcessId, recommendedCarrierSlug);
  };

  const saveContact = (orderPayload: Order) => {
    const contactPayload = { ...orderPayload.contact } as ContactRequest;

    mutateAddNewContact(contactPayload, {
      onSuccess: () => {
        queryClient.refetchQueries(['contacts']);
        message?.success(t('contact_added'));
      },
      onError: () => { message?.error(t('operation_failed_please_try_again')); },
    });
  };

  const openNotVerifiedConfirm = () => {
    openConfirm({
      description: t('user_not_verified'),
      onConfirm: () => {
        trackEvent(
          'request_verification',
          {
            userId: userData?.object?.uid,
            traits: {
              phone: userData?.object?.phone,
              user_type: 'MERCHANT',
              email: userData?.object?.email,
              firstname: userData?.object?.first_name,
              lastname: userData?.object?.last_name,
              fullname: `${userData?.object?.first_name}${userData?.object?.last_name}`,
            },
          },
          t('thank_you_for_choosing_Boxy_we_will_contact_you_soon'),
        );
      },
      approveText: t('request_activation'),
      cancelText: t('cancel'),
    });
  };

  const handleCreateProduct = () => {
    handleAddProduct({ title: '', quantity: '1', price: '' });
  };

  // Auto-derive order-level fragile from products — set true when any is fragile, clear when none are.
  // Suppressed during back-nav restore so it can't clobber the restored isFragile mid-hydration.
  useEffect(() => {
    if (!fragileSyncEnabledRef.current) return;
    const anyFragile = products.some((p) => p.is_fragile === true);
    const currentFragile = form.getFieldValue('isFragile');
    if (anyFragile && !currentFragile) {
      form.setFieldsValue({ isFragile: true });
    } else if (!anyFragile && currentFragile) {
      form.setFieldsValue({ isFragile: false });
    }
  }, [products]);

  const handleSetSelectedProvinceCode = (provinceCode?: string) => {
    form.setFieldsValue({ province: provinceCode, region_uid: undefined });
    form.setFieldValue('region_uid', undefined);

    setSelectedProvinceCode(provinceCode);
  };

  const handleFormUpdateForAddress = (formValue: Store) => {
    handleFormUpdated(
      { saved_pick_up_address_uid: formValue.saved_pick_up_address_uid },
      { ...formValue },
    );
  };

  const handleOrderTypeChange = (newOrderType: OrderTypeEnum) => {
    const allValues = form.getFieldsValue();
    const updatedProducts = adjustProductListByOrderType(products, newOrderType);
    setProducts(updatedProducts);
    handleFormUpdated(
      { orderType: newOrderType },
      { ...allValues, orderType: newOrderType, products: updatedProducts },
    );
  };

  const onFormValuesChange = (changedValues: any, allValues: Store) => {
    const changedItem = Object.keys(changedValues)[0];

    if (changedItem === 'orderType') {
      const updatedProducts = adjustProductListByOrderType(products, allValues.orderType);
      setProducts(updatedProducts);
      handleFormUpdated(changedValues, { ...allValues, products: updatedProducts });
    } else {
      handleFormUpdated(changedValues, allValues);
    }
  };

  const handleUpdatePreviewByContactSelection = (contact: ContactData) => {
    const allValues = form.getFieldsValue();

    handleFormUpdated({ address: contact.address || '' }, { ...allValues, ...contact });
  };

  // Task-6 — the generic NoCarrierModal should name the destination
  // province when the true cause is "no temperature-control carrier serves
  // this route". Display-only: does not affect eligibility/submit logic.
  const noCarrierLocale = i18n.language?.startsWith('ar') ? 'ar' : 'en';
  const temperatureUnavailableProvince =
    form.getFieldValue('temperatureControl') && selectedProvinceCode
      ? resolveProvinceName(selectedProvinceCode, provinceList?.object, noCarrierLocale)
      : undefined;

  return (
    <div className='sd-scope sd-font relative flex flex-col -mx-4 sm:-mx-8 -mt-6 -mb-6 h-[calc(100dvh-65px)] overflow-hidden'>
      <NoCarrierModal
        open={showNoCarrierModal}
        onClose={() => setShowNoCarrierModal(false)}
        recommendedCarrierSlug={recommendedCarrierSlug}
        temperatureUnavailableProvince={temperatureUnavailableProvince}
      />
      {/* NEW-2 — rule mandates X but X is unavailable for this route */}
      <NoCarrierModal
        open={!!ruleMandateUnavailable}
        onClose={() => setRuleMandateUnavailable(null)}
        ruleMandate={ruleMandateUnavailable ?? undefined}
        onPickManual={() => {
          // Override the rule for this single order: clear the rule
          // dispatch context and rerun the carrier fetch as 'manual'.
          setRuleMandateUnavailable(null);
          const formValues = form.getFieldsValue();
          const orderPayloadCurrent = transformValuesToOrder(
            { ...formValues, products: products.filter((p) => checkProductValidation(p)) },
            false,
            true,
          );
          const sourceUid = resolveSourceRegionUid(formValues);
          if (orderPayloadCurrent && sourceUid) {
            proceedWithCarrierFetch(orderPayloadCurrent, false, sourceUid, {
              action: 'manual',
              assignedCarrierUid: null,
              winningRuleUid: null,
              restrictedCarrierUids: [],
              recommendedFilter: null,
            });
          }
        }}
      />
      <div ref={scrollRef} className='flex-1 overflow-y-auto px-4 sm:px-8 pt-4 sm:pt-6 pb-40'>
        <Form
          form={form}
          initialValues={{
            packageSize: 'S',
            CustomerOrMerchant: ShipmentFeeType.BY_MERCHANT,
            CodOrPrepaid: PaymentType.COLLECT_ON_DELIVERY,
          }}
          name='add-new-order'
          onValuesChange={onFormValuesChange}
        >
          {/* Breadcrumb navigation */}
          <nav className='flex items-center gap-1.5 text-sm text-sd-muted-foreground mb-6'>
            <Link to='/orders' className='text-sd-muted-foreground hover:text-sd-foreground transition-colors no-underline'>
              {String(t('orders'))}
            </Link>
            <ChevronRight className={`h-3.5 w-3.5 ${isRTL ? 'rotate-180' : ''}`} />
            <span className='text-sd-foreground font-medium'>{String(t('new_order'))}</span>
          </nav>

          <OrderStepper currentStep={1} />

          <div className='grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6'>
            {/* Left column */}
            <div className='flex flex-col gap-6'>
              <CustomerDetailsCard
                form={form}
                handleUpdatePreview={handleUpdatePreviewByContactSelection}
                setSelectedProvinceCode={handleSetSelectedProvinceCode}
                selectedProvinceCode={selectedProvinceCode}
              />
              <DeliveryInstructionCard />
              <OrderIdCard />
              <ProductDetailsCard
                products={products}
                handleCreateProduct={handleCreateProduct}
                handleDeleteProduct={handleDeleteProduct}
                handleUpdateProduct={handleUpdateProduct}
                handleUpdateProductBatch={handleUpdateProductBatch}
                handleAddProducts={handleAddProducts}
              />
              <PickupAddressCard form={form} previewOrderPrice={handleFormUpdateForAddress} />
            </div>
            {/* Right column */}
            <div className='flex flex-col gap-6'>
              <div className='flex flex-col gap-6'>
                <PackageAndPaymentCard
                  orderOptions={[]}
                  form={form}
                  onPackageSizeChange={setPackageSize}
                  orderPrice={{ ...orderPrice, orders_shipment_fee: 0, orders_custom_shipment_fee: 0 }}
                  orderPriceLoading={orderPriceLoading}
                  onCustomShipmentFeeChange={setCustomShipmentFee}
                  customShipmentFee={customShipmentFee}
                  onOrderTypeChange={handleOrderTypeChange}
                  hasFragileProduct={products.some((p) => p.is_fragile === true)}
                />
              </div>
            </div>
          </div>
        </Form>
      </div>

      {/* Floating Footer */}
      <div className='absolute bottom-4 start-0 end-0 z-30 px-4 sm:px-8'>
        <div className={cn(
          'rounded-2xl border border-sd-border bg-sd-card px-5 py-4 flex items-center justify-end gap-3 transition-shadow',
          isScrolledUp && 'shadow-lg',
        )}>
          {orderType !== OrderTypeEnum.NORMAL && (
            <span className='text-sm font-bold uppercase text-sd-foreground'>
              {String(t(getOrderTypeTextKey(orderType)))}
            </span>
          )}
          <button
            onClick={() => handleSubmit(true)}
            disabled={!!addOrderLoading}
            className='inline-flex items-center justify-center gap-2 h-10 px-4 py-2 rounded-md bg-sd-primary text-sm font-medium uppercase text-sd-primary-foreground hover:bg-sd-primary/90 disabled:opacity-50 transition-colors'
          >
            {addOrderLoading === 'creating' ? (
              <Loader2 className='h-4 w-4 animate-spin' />
            ) : (
              <>
                {String(t('next', 'NEXT'))}
                <ArrowRight className={`h-4 w-4 ${isRTL ? 'rotate-180' : ''}`} strokeWidth={2} />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddNewOrder;
